export interface LayerStyle {
  kind: 'hazard' | 'underground';
  /** Color mode: 'uniform' (single color) or 'gradient' (mapped from a feature attribute) */
  colorMode: 'uniform' | 'gradient';
  /** For uniform mode */
  color?: string;
  /** For gradient mode: stops in [value, color] form */
  gradientStops?: Array<[number, string]>;
  /** For gradient mode: which batch-table attribute provides the value */
  gradientAttribute?: string;
  /** Default opacity */
  opacity?: number;
}

/**
 * Built-in overlay styles keyed by feature class detected from layer label or layerId.
 * Provides default color mapping for PLATEAU hazard + underground overlays without
 * requiring external configuration files.
 *
 * Opacity defaults raised to 0.85 (from the original 0.6) so that flat hazard
 * polygons sitting near ground level stay clearly visible when the user toggles
 * them on; the original 0.6 made flood / high-tide polygons effectively invisible
 * against the textured basemap.
 */
const BUILT_IN_HAZARD_STYLES: Record<string, LayerStyle> = {
  fld: {
    kind: 'hazard',
    colorMode: 'gradient',
    gradientAttribute: '_浸水深',
    gradientStops: [
      [0, '#a3d2ff'],   // Light blue for shallow water
      [1, '#3787ff'],   // Medium blue for 1m depth
      [3, '#ff7e00'],   // Orange for 3m depth
      [5, '#d80000'],   // Red for 5m+ depth
    ],
    opacity: 0.85,
  },
  htd: {
    kind: 'hazard',
    colorMode: 'uniform',
    color: '#00ccff',
    opacity: 0.85,
  },
  lsld: {
    kind: 'hazard',
    colorMode: 'uniform',
    color: '#ffd700',
    opacity: 0.85,
  },
  // Underground buildings (ubld) — rendered with a translucent dark grey so
  // they're clearly distinguishable when the camera is positioned underneath
  // the ground plane (orbit-down or section-cut workflow). Without this they
  // inherit the standard PBR material and become hard to tell apart from
  // above-ground buildings when both are visible.
  ubld: {
    kind: 'underground',
    colorMode: 'uniform',
    color: '#4a4a55',
    opacity: 0.7,
  },
};

/**
 * Extract feature class from layer ID or label.
 * Looks for known PLATEAU hazard prefixes: fld (flood), htd (high-tide), lsld (landslide).
 */
function extractFeatureClass(layerId: string, layerLabel?: string): string | null {
  const text = `${layerId} ${layerLabel || ''}`.toLowerCase();

  if (text.includes('underground') || /\bubld\b/.test(text)) return 'ubld';
  if (text.includes('flood') || text.includes('fld')) return 'fld';
  if (text.includes('high-tide') || text.includes('htd')) return 'htd';
  if (text.includes('landslide') || text.includes('lsld')) return 'lsld';

  return null;
}

/**
 * Resolve layer style for a given layer. Returns the built-in style for recognized
 * hazard types, or null if no style should be applied.
 */
export function resolveLayerStyle(layerId: string, layerLabel?: string): LayerStyle | null {
  const featureClass = extractFeatureClass(layerId, layerLabel);
  if (!featureClass) return null;
  
  return BUILT_IN_HAZARD_STYLES[featureClass] || null;
}

/**
 * Get uniform color from a layer style, or return a fallback color if using gradient mode.
 * For gradient styles, returns the mid-point color as a reasonable uniform fallback.
 */
export function getUniformColor(style: LayerStyle): string {
  if (style.colorMode === 'uniform' && style.color) {
    return style.color;
  }
  
  if (style.colorMode === 'gradient' && style.gradientStops && style.gradientStops.length > 0) {
    // Return the middle stop color as a reasonable uniform fallback
    const middleIndex = Math.floor(style.gradientStops.length / 2);
    return style.gradientStops[middleIndex][1];
  }
  
  // Final fallback - semi-transparent blue
  return '#3787ff';
}

/**
 * TODO(M8-gradient): Implement per-feature gradient coloring.
 * This function should walk the batch table, extract values from gradientAttribute,
 * map them to colors using gradientStops, and write vertex colors to the geometry.
 * For now, returns uniform color as fallback.
 */
export function applyGradientColoring(
  _entity: any, 
  _style: LayerStyle,
  _batchTable?: Record<string, any>
): void {
  console.warn('[layerStyle] Gradient coloring not yet implemented. Using uniform color fallback.');
  // TODO: Implement batch table walking and per-feature color mapping
}